/**
 * Auth Pages - Login and Signup
 */
import { useState } from 'react'
import { Link } from '@/components/ui/link'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import { useTranslations } from '@/hooks/use-translations'

export function LoginPage() {
    const { signIn, isLoading } = useAuth()
    const t = useTranslations('auth')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setIsSubmitting(true)

        try {
            await signIn(email, password)
        } catch (err) {
            if (err instanceof Error) {
                if (err.message === 'USER_NOT_FOUND') {
                    setError(t('userNotFound'))
                } else {
                    setError(err.message)
                }
            } else {
                setError(t('failedSignIn'))
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="animate-fade-in">
            <Card className='shadow-md'>
                <CardHeader className="text-center">
                    <div className="flex justify-center mb-6">
                        <img
                            src="/login_logo.svg"
                            alt="Minora"
                            className="h-16 w-auto"
                        />
                    </div>
                    <CardTitle className="text-2xl text-text-primary">{t('welcomeBack')}</CardTitle>
                    <CardDescription className='text-text-primary'>
                        {t('signInTitle')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="rounded-xl bg-danger-bg p-3 text-sm text-danger">
                                {error}
                            </div>
                        )}

                        <Input
                            label={t('email')}
                            type="email"
                            placeholder={t('emailPlaceholder')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                        />

                        <div className="relative">
                            <Input
                                className='pr-10'
                                label={t('password')}
                                type={showPassword ? 'text' : 'password'}
                                placeholder={t('passwordPlaceholder')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-8"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </Button>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                            <label className="flex items-center gap-2">
                                <Checkbox />
                                <span className="text-text-primary">{t('rememberMe')}</span>
                            </label>
                            <Link
                                href="/forgot-password"
                                className="text-text-link underline underline-offset-2"
                            >
                                {t('forgotPassword')}
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={isSubmitting || isLoading}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    {t('signingIn')}
                                </>
                            ) : (
                                t('signIn')
                            )}
                        </Button>
                    </form>

                    {/* <div className="mt-6 text-center text-sm text-text-primary">
                        {t('noAccount')}{' '}
                        <Link
                            href="/signup"
                            className="font-medium text-text-primary hover:text-text-primary transition-colors"
                        >
                            {t('signUp')}
                        </Link>
                    </div> */}
                </CardContent>
            </Card>
        </div>
    )
}

